// ATLAS · S.T.A.G.E. table — one connected block, a row per stage: label on
// the left, the answer in a pill, a chevron on the right. Tap a row to open
// its option list right under it (one open at a time).
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { ChevronDown, ChevronLeft } from 'lucide-react-native';
import { useTheme, makeThemedStyles } from '../../v10/theme';
import { useTourTarget } from '../../v10/onboarding';
import { DropdownMenu, type DropdownOption } from './AtlasDropdown';

export interface StageRow {
  key: string;
  /** Onboarding tour target ids: the row's trigger and its open option list. */
  tourId?: string;
  menuTourId?: string;
  label: string;
  value: string | null;
  placeholder: string;
  options: DropdownOption[];
  selected: string[];
  multi?: boolean;
  onPick: (key: string) => void;
  onAddCustom?: (text: string) => void;
  customPlaceholder?: string;
  onEdit?: (key: string, label: string) => void;
  onDelete?: (key: string) => void;
}

/** #rrggbb + alpha → rgba() */
const tint = (hex: string, a: number) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

function TourWrap({ id, children }: { id?: string; children: React.ReactNode }) {
  const tt = useTourTarget(id);
  return id ? <View ref={tt.onLayoutRef} collapsable={false}>{children}</View> : <>{children}</>;
}

export function AtlasStageTable({
  rows,
  open,
  setOpen,
  accent,
}: {
  rows: StageRow[];
  open: string | null;
  setOpen: (key: string | null) => void;
  accent: string;
}) {
  const styles = useStyles();
  const { t } = useTheme();
  return (
    <View style={styles.table}>
      {rows.map((r, i) => {
        const isOpen = open === r.key;
        const Chev = isOpen ? ChevronDown : ChevronLeft;
        return (
          <View key={r.key} style={[i > 0 && styles.divider, { backgroundColor: tint(accent, 0.3 - i * 0.045) }]}>
            <TourWrap id={r.tourId}>
            <Pressable style={styles.row} onPress={() => setOpen(isOpen ? null : r.key)} accessibilityRole="button" accessibilityLabel={r.label}>
              <Text style={styles.label}>{r.label}</Text>
              <View style={[styles.pill, isOpen && { borderColor: t.gold }]}>
                <Text style={[styles.value, !r.value && styles.placeholder]} numberOfLines={2}>
                  {r.value || r.placeholder}
                </Text>
              </View>
              <Chev size={16} color={accent} strokeWidth={3} />
            </Pressable>
            </TourWrap>
            {isOpen ? (
              <TourWrap id={r.menuTourId}>
              <View style={styles.menuWrap}>
                <DropdownMenu
                  options={r.options}
                  selected={r.selected}
                  multi={r.multi}
                  onPick={r.onPick}
                  onClose={() => setOpen(null)}
                  onAddCustom={r.onAddCustom}
                  customPlaceholder={r.customPlaceholder}
                  onEdit={r.onEdit}
                  onDelete={r.onDelete}
                />
              </View>
              </TourWrap>
            ) : null}
          </View>
        );
      })}
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, c }) =>
  StyleSheet.create({
    table: { borderRadius: 22, overflow: 'hidden', borderWidth: 1, borderColor: t.navyLine, backgroundColor: t.navyRaised },
    divider: { borderTopWidth: 1, borderTopColor: t.lineFaint },
    row: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 8, paddingLeft: 14, paddingRight: 12 },
    label: { width: 98, fontSize: 13, fontWeight: '900', letterSpacing: 1, color: t.ivory, textTransform: 'uppercase' },
    pill: {
      flex: 1,
      minHeight: 38,
      justifyContent: 'center',
      paddingHorizontal: 14,
      paddingVertical: 7,
      borderRadius: 999,
      borderWidth: 1,
      borderColor: t.navyLine,
      backgroundColor: c.inputFill,
    },
    value: { fontSize: 13.5, color: t.ivory, lineHeight: 18 },
    placeholder: { color: t.ivoryFaint },
    menuWrap: { paddingHorizontal: 10, paddingBottom: 10, marginTop: -6 },
  }),
);
